import { Application, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { gsap } from 'gsap';
import { GameLoop } from '../engine/GameLoop';
import { Input } from '../engine/Input';
import { aabbOverlap, moveAndCollide, type AABB } from '../engine/Physics';
import { Boss, type BossContext } from './Boss';
import { Bullet } from './Bullet';
import { Camera } from './Camera';
import { createEnemy, type Enemy, type EnemyContext } from './enemies';
import { Level, type LevelAssets } from './Level';
import { Player } from './Player';
import { FlagSystem } from './FlagSystem';
import { Dummy } from './Dummy';
import { TestBot } from './TestBot';
import { getCurrentNickname } from './nicknames';
import type { LevelsData } from '../types';
import type { BulletState, GamePlayerSnapshot, GamePlayerState, MatchPlayerStats, PlayerId, PlayerSkin } from 'shared';
import type { OnlineSession } from '../net/online';

const RESPAWN_DELAY_MS = 600;
// Шлём своё состояние ~60Гц чтобы соответствовать тик-рейту сервера: иначе
// remote players на других клиентах двигаются с шагом 50мс (видно как 20fps).
const ONLINE_SEND_INTERVAL_MS = 16;
// Интерполяция удалённых игроков: рендерим их с задержкой INTERP_DELAY_MS,
// чтобы всегда иметь «следующий» снапшот для интерполяции. 100мс — стандарт
// (Source/Quake-подобные движки); абсорбирует джиттер до 3 пропущенных снапшотов
// при 30Гц или 6 при 60Гц. Чем больше, тем плавнее, но «прошлее» в реал-тайме.
const INTERP_DELAY_MS = 100;
// Максимальный размер кольцевого буфера снапшотов на игрока — больше нет смысла
// (старые точки никогда не понадобятся, мы рендерим всего ~100мс назад).
const INTERP_BUFFER_LIMIT = 16;

type InterpSample = {
  /** локальное время прихода снапшота (performance.now()) */
  tMs: number;
  x: number;
  y: number;
  facing: -1 | 1;
  aimAngle: number;
};

const clamp01 = (v: number): number => v < 0 ? 0 : v > 1 ? 1 : v;

/** Кратчайшая угловая интерполяция, обрабатывает wrap через ±π. */
const lerpAngle = (a: number, b: number, t: number): number => {
  let diff = b - a;
  if (diff > Math.PI) diff -= Math.PI * 2;
  else if (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
};

// Базовые громкости отдельных звуков и музыки. Финальная громкость = base * masterVolume,
// где master выставляется ползунком в настройках (0..1).
const SFX_BASE = {
  walk: 0.5,
  vic: 0.4,
  reload: 0.23,
  pew: 0.5,
  jump: 0.3,
  hit: 0.7,
  dea: 0.48,
  lose: 0.4,
} as const;
const MUSIC_BASE = 1.0;

function hex(color: string): number {
  return parseInt(color.replace('#', ''), 16);
}

export class Game {
  private input: Input;
  private loop: GameLoop;
  private camera: Camera;

  private bg = new Graphics();
  private bgLayer = new Container(); // отдельный слой для фонового спрайта
  private bgSprite: Sprite | null = null;
  private world = new Container();
  private enemiesLayer = new Container();
  private bulletsLayer = new Container();
  private playerView = new Container();
  private playerSprite!: Sprite;
  private rifleSprite!: Sprite;
  private hud = new Container();
  private deathMsg: Text;
  private respawnCountdown: Text;
  private coordsText: Text;
  private deathCounter: Text;
  private levelLabel: Text;
  private onlineStatusText: Text;
  private pingBg: Graphics;
  private pingText: Text;

  private level!: Level;
  private player: Player;
  private enemies: Enemy[] = [];
  private bullets: Bullet[] = [];
  private boss: Boss | null = null;
  private flagLayer = new Container();
  private dummy: Dummy;
  private testBots: TestBot[] = [];
  private walkSound: HTMLAudioElement;
  private vicSound: HTMLAudioElement;
  private reloadSound: HTMLAudioElement;
  private pewSound: HTMLAudioElement;
  private jumpSound: HTMLAudioElement;
  private hitSound: HTMLAudioElement;
  private deaSound: HTMLAudioElement;
  private loseSound: HTMLAudioElement;
  private musicIntro!: HTMLAudioElement;
  private musicB2!: HTMLAudioElement;
  private musicB3!: HTMLAudioElement;
  private started = false;
  private lang: 'ru' | 'en' = 'en';
  private playerSkinUrl = '';
  private usedSkinIndices: Set<number> = new Set();
  private exitCallback: (() => void) | null = null;
  private escapeCooldown = 0;
  private exitOverlay: HTMLDivElement | null = null;
  private leaveButton: HTMLButtonElement | null = null;
  private musicEnabled = true;
  private soundEnabled = true;
  private musicVolume = 1;
  private soundVolume = 1;
  private musicLoop!: HTMLAudioElement;

  private flagSystem: FlagSystem | null = null;
  private scoreText: Text;
  private timerText: Text;
  private matchTimer = 0; // ms remaining
  private scoreRed = 0;   // pvp kills
  private scoreBlue = 0;  // pvp kills
  private tabOverlay: HTMLDivElement | null = null;
  private matchStats: { nick: string; team: 'red'|'blue'; kills: number; deaths: number }[] = [];
  private won = false;
  private deaths = 0;
  private levelIndex = 0;
  private spawnPoint = { x: 0, y: 0 };
  private respawnTimer = 0;
  private playerCarrier: import('./MovingPlatform').MovingPlatform | null = null;
  private bossLayer = new Container();
  private bossHpBg = new Graphics();
  private bossHpFill = new Graphics();
  private ammoText: Text;
  private reloadBg = new Graphics();
  private reloadFill = new Graphics();
  private hpBarBg = new Graphics();
  private hpBarFill = new Graphics();
  private nickText: Text;
  private reloadCircle = new Graphics();
  private bossLabel: Text;
  private onlineSession: OnlineSession | null = null;
  private onlineSendTimerMs = 0;
  private remoteSnapshots = new Map<PlayerId, GamePlayerSnapshot>();
  private remoteStats = new Map<PlayerId, MatchPlayerStats>(); // K/D с сервера
  private remotePlayers = new Map<PlayerId, {
    view: Container;
    sprite: Sprite;
    rifle: Sprite;
    hpBg: Graphics;
    hpFill: Graphics;
    nameText: Text;
    skin: PlayerSkin;
    hp: number;
    /** Кольцевой буфер последних снапшот-семплов для интерполяции. */
    buffer: InterpSample[];
    /** Интерполированные мировые координаты (top-left хитбокса) после updateRemoteInterpolation.
     *  Используются ВСЕМ остальным кодом (push, локальный bullet hit-check) чтобы коллизии
     *  совпадали с тем, что игрок видит на экране. */
    interpX: number;
    interpY: number;
    alive: boolean;
  }>();
  private remoteBullets = new Map<string, Graphics>();
  /** Удалённые пули, локально убитые столкновением с платформой — не рендерим даже если сервер ещё шлёт. */
  private deadRemoteBullets = new Set<string>();

  constructor(
    private app: Application,
    private data: LevelsData,
    private assets: { cucumberUrl: string; skinUrls: string[]; skinUrlsById: Record<PlayerSkin, string>; rifleUrl: string; arena1BgUrl: string; platformHUrl: string; platformVUrl: string; platformMUrl: string; spikeUrl: string; walkUrl: string; vicUrl: string; reloadUrl: string; pewUrl: string; jumpUrl: string; hitUrl: string; deaUrl: string; loseUrl: string; musicIntroUrl: string; musicB2Url: string; musicB3Url: string; musicLoopUrl: string; flagRedUrl: string; flagBlueUrl: string },
  ) {
    this.input = new Input(app.canvas as HTMLCanvasElement, app.screen.width, app.screen.height);
    this.camera = new Camera(data.config.screenWidth, data.config.screenHeight);

    app.stage.addChild(this.bg);
    app.stage.addChild(this.bgLayer);
    app.stage.addChild(this.world);
    this.world.addChild(this.flagLayer);
    this.world.addChild(this.bossLayer);
    this.world.addChild(this.enemiesLayer);
    this.world.addChild(this.bulletsLayer);
    this.world.addChild(this.playerView);
    app.stage.addChild(this.hud);

    this.coordsText = new Text({
      text: '',
      style: { fontFamily: 'monospace', fontSize: 14, fill: 0xffff00 }
    });
    this.coordsText.x = 8;
    this.coordsText.y = 72;
    // Демо: координаты слева скрыты от игрока.
    this.coordsText.visible = false;
    this.hud.addChild(this.coordsText);

    this.dummy = new Dummy(assets.cucumberUrl);
    this.walkSound = new Audio(assets.walkUrl);
    this.walkSound.preload = 'none';
    this.walkSound.loop = true;
    this.walkSound.volume = SFX_BASE.walk * this.soundVolume;
    this.vicSound = new Audio(assets.vicUrl);
    this.vicSound.preload = 'none';
    this.vicSound.volume = SFX_BASE.vic * this.soundVolume;
    this.reloadSound = new Audio(assets.reloadUrl);
    this.reloadSound.preload = 'none';
    this.reloadSound.loop = true;
    this.reloadSound.volume = SFX_BASE.reload * this.soundVolume;
    this.pewSound = new Audio(assets.pewUrl);
    this.pewSound.preload = 'none';
    this.pewSound.volume = SFX_BASE.pew * this.soundVolume;
    this.jumpSound = new Audio(assets.jumpUrl);
    this.jumpSound.preload = 'none';
    this.jumpSound.volume = SFX_BASE.jump * this.soundVolume;
    this.hitSound = new Audio(assets.hitUrl);
    this.hitSound.preload = 'none';
    this.hitSound.volume = SFX_BASE.hit * this.soundVolume;
    this.deaSound = new Audio(assets.deaUrl);
    this.deaSound.preload = 'none';
    this.deaSound.volume = SFX_BASE.dea * this.soundVolume;
    this.loseSound = new Audio(assets.loseUrl);
    this.loseSound.preload = 'none';
    this.loseSound.volume = SFX_BASE.lose * this.soundVolume;
    this.musicIntro = new Audio(assets.musicIntroUrl);
    this.musicIntro.preload = 'none';
    this.musicIntro.volume = MUSIC_BASE * this.musicVolume;
    this.musicB3 = new Audio(assets.musicB3Url);
    this.musicB3.preload = 'none';
    this.musicB3.volume = MUSIC_BASE * this.musicVolume;
    this.musicB2 = new Audio(assets.musicB2Url);
    this.musicB2.preload = 'none';
    this.musicB2.volume = MUSIC_BASE * this.musicVolume;
    this.musicLoop = new Audio(assets.musicLoopUrl);
    this.musicLoop.preload = 'none';
    this.musicLoop.volume = MUSIC_BASE * this.musicVolume;
    this.musicLoop.loop = true;
    // цепочка: intro → b3 → b2 → loop (зацикл.)
    this.musicIntro.addEventListener('ended', () => {
      this.musicB3.currentTime = 0;
      this.musicB3.play().catch(() => {});
    });
    this.musicB3.addEventListener('ended', () => {
      this.musicB2.currentTime = 0;
      this.musicB2.play().catch(() => {});
    });
    this.musicB2.addEventListener('ended', () => {
      this.musicLoop.currentTime = 0;
      this.musicLoop.play().catch(() => {});
    });
    this.world.addChild(this.dummy.view);

    this.timerText = new Text({
      text: '10:00',
      style: { fontFamily: 'Arial', fontSize: 18, fontWeight: 'bold', fill: 0xffffff, stroke: { color: 0x000000, width: 4 } }
    });
    this.timerText.anchor.set(0.5, 0);
    this.timerText.x = data.config.screenWidth / 2;
    this.timerText.y = 8;
    this.timerText.visible = false;
    this.hud.addChild(this.timerText);

    this.scoreText = new Text({
      text: '0 : 0',
      style: { fontFamily: 'Arial', fontSize: 28, fontWeight: 'bold', fill: 0xffffff, stroke: { color: 0x000000, width: 4 } }
    });
    this.scoreText.anchor.set(0.5, 0);
    this.scoreText.x = data.config.screenWidth / 2;
    this.scoreText.y = 30;
    this.scoreText.visible = false;
    this.hud.addChild(this.scoreText);

    this.respawnCountdown = new Text({
      text: '',
      style: { fontFamily: 'Arial', fontSize: 48, fontWeight: 'bold', fill: 0xffffff, stroke: { color: 0x000000, width: 6 } }
    });
    this.respawnCountdown.anchor.set(0.5);
    this.respawnCountdown.x = data.config.screenWidth / 2;
    this.respawnCountdown.y = data.config.screenHeight / 2 + 80;
    this.respawnCountdown.visible = false;
    this.hud.addChild(this.respawnCountdown);

    this.deathMsg = new Text({
      text: '',
      style: { fill: 0xffffff, fontSize: 36, fontFamily: 'Arial', fontWeight: 'bold', stroke: { color: 0x000000, width: 4 } },
    });
    this.deathMsg.anchor.set(0.5);
    this.deathMsg.x = data.config.screenWidth / 2;
    this.deathMsg.y = data.config.screenHeight / 2;
    this.deathMsg.visible = false;

    this.deathCounter = new Text({
      text: 'Смертей: 0',
      style: { fill: 0xffffff, fontSize: 18, fontFamily: 'Arial', stroke: { color: 0x000000, width: 3 } },
    });
    this.deathCounter.x = 16;
    this.deathCounter.y = 12;
    // Демо: счётчик смертей слева скрыт от игрока.
    this.deathCounter.visible = false;

    this.levelLabel = new Text({
      text: '',
      style: { fill: 0xffffff, fontSize: 18, fontFamily: 'Arial', stroke: { color: 0x000000, width: 3 } },
    });
    this.levelLabel.x = 16;
    this.levelLabel.y = 36;
    // Демо: лейбл уровня слева скрыт от игрока.
    this.levelLabel.visible = false;

    this.onlineStatusText = new Text({
      text: '',
      style: { fill: 0x8fd7ff, fontSize: 15, fontFamily: 'Arial', fontWeight: 'bold', stroke: { color: 0x000000, width: 3 } },
    });
    this.onlineStatusText.x = 16;
    this.onlineStatusText.y = 60;
    this.onlineStatusText.visible = false;

    this.pingBg = new Graphics();
    this.pingBg.visible = false;

    this.pingText = new Text({
      text: 'пинг -- мс',
      style: { fill: 0x9dff9d, fontSize: 14, fontFamily: 'Arial', fontWeight: 'bold', stroke: { color: 0x000000, width: 3 } },
    });
    this.pingText.anchor.set(1, 0);
    this.pingText.x = data.config.screenWidth - 16;
    this.pingText.y = 12;
    this.pingText.visible = false;

    this.bossLabel = new Text({
      text: '',
      style: { fill: 0xffffff, fontSize: 16, fontFamily: 'Arial', fontWeight: 'bold', stroke: { color: 0x000000, width: 3 } },
    });
    this.bossLabel.anchor.set(0.5, 0);
    this.bossLabel.x = data.config.screenWidth / 2;
    this.bossLabel.y = 8;
    this.bossLabel.visible = false;

    this.hud.addChild(this.deathMsg);
    this.hud.addChild(this.deathCounter);
    this.hud.addChild(this.levelLabel);
    this.hud.addChild(this.onlineStatusText);
    this.hud.addChild(this.pingBg);
    this.hud.addChild(this.pingText);
    this.hud.addChild(this.bossLabel);
    this.ammoText = new Text({
      text: '●●●●●',
      style: { fontFamily: 'monospace', fontSize: 18, fill: 0xffffff, stroke: { color: 0x000000, width: 3 } }
    });
    this.ammoText.anchor.set(0.5, 0.5);
    this.hud.addChild(this.bossHpBg);
    this.hud.addChild(this.bossHpFill);
    this.hud.addChild(this.ammoText);
    this.nickText = new Text({
      text: '',
      style: { fontFamily: 'Arial', fontSize: 11, fontWeight: 'bold', fill: 0x44ff88, stroke: { color: 0x000000, width: 3 } }
    });
    this.nickText.anchor.set(0.5, 1);
    this.nickText.visible = false;
    this.hud.addChild(this.nickText);
    this.hud.addChild(this.hpBarBg);
    this.hud.addChild(this.hpBarFill);
    this.hud.addChild(this.reloadCircle);
    this.hud.addChild(this.reloadBg);
    this.hud.addChild(this.reloadFill);

    this.player = new Player(data.config);
    this.drawPlayer();

    this.loadLevel(0);

    this.loop = new GameLoop(
      () => this.fixedStep(),
      () => this.render(),
    );
  }

  start(): void {
    this.started = true;
    this.loop.start();
  }

  destroy(): void {
    this.loop.stop();
    this.input.destroy();
    this.onlineSession?.close();
    this.hideLeaveButton();
  }

  setOnlineSession(session: OnlineSession): void {
    this.onlineSession?.close();
    this.onlineSession = session;
    this.onlineStatusText.text = `ONLINE ${session.roomId} · ${session.playerId}`;
    // Демо: online status слева скрыт от игрока.
    this.onlineStatusText.visible = false;
    this.updatePingText(session.pingMs);
    session.onPing(pingMs => this.updatePingText(pingMs));
    // Кнопка «Покинуть» в углу — единственный явный путь выйти из онлайн-комнаты,
    // т.к. серверный soft-disconnect держит запись 5 минут при разрыве WS.
    this.showLeaveButton();
    this.applyOwnOnlineSkin(session.room);
    session.onRoomState(room => this.applyOwnOnlineSkin(room));
    session.onSnapshot(snapshot => {
      this.applyOnlineSnapshot(snapshot.players);
      this.applyOnlineBullets(snapshot.bullets);
      // Сервер авторитетен по score+timer — берём из снапшота
      this.scoreRed = snapshot.scores.red;
      this.scoreBlue = snapshot.scores.blue;
      this.scoreText.text = this.scoreRed + ' : ' + this.scoreBlue;
      this.scoreText.visible = true;
      this.matchTimer = snapshot.matchTimeLeftMs;
      // K/D статистика — server-authoritative, обновляем remoteStats
      // и подтягиваем свои в matchStats[0]
      this.applyMatchStats(snapshot.playerStats);
      const ownStats = snapshot.playerStats.find(s => s.playerId === session.playerId);
      if (ownStats && this.matchStats[0]) {
        this.matchStats[0].kills = ownStats.kills;
        this.matchStats[0].deaths = ownStats.deaths;
      }
    });
    session.onEvent(event => {
      if (event.type === 'damage') {
        // визуальный импакт у цели (свой или удалённой)
        let hx: number | undefined;
        let hy: number | undefined;
        if (event.targetId === session.playerId) {
          hx = this.player.pos.x + this.player.width / 2;
          hy = this.player.pos.y + this.player.height / 2;
          this.player.hp = event.hpAfter;
          if (this.soundEnabled) { this.hitSound.currentTime = 0; this.hitSound.play().catch(() => {}); }
        } else {
          const remote = this.remotePlayers.get(event.targetId);
          if (remote) { hx = remote.view.x; hy = remote.view.y; }
        }
        if (hx !== undefined && hy !== undefined) this.spawnHitCross(hx, hy);
      } else if (event.type === 'kill' && event.victimId === session.playerId) {
        this.player.hp = 0;
        if (this.player.alive) this.kill();
      }
    });
  }

  private updatePingText(pingMs: number | null): void {
    const visible = Boolean(this.onlineSession);
    this.pingText.visible = visible;
    this.pingBg.visible = visible;
    if (pingMs === null) {
      this.pingText.text = 'пинг -- мс';
      this.pingText.style.fill = 0x9aa3ad;
    } else {
      const color = pingMs <= 80 ? 0x9dff9d : pingMs <= 140 ? 0xffdf76 : 0xff6b6b;
      this.pingText.text = `пинг ${pingMs} мс`;
      this.pingText.style.fill = color;
    }

    this.pingBg
      .clear()
      .roundRect(this.pingText.x - this.pingText.width - 12, 8, this.pingText.width + 24, 26, 6)
      .fill({ color: 0x06090f, alpha: 0.58 })
      .stroke({ color: 0xffffff, width: 1, alpha: 0.14 });
  }

  /** Dev-only: jump to a specific level (1-based). */
  gotoLevel(levelNumber: number): void {
    const idx = Math.max(0, Math.min(this.data.levels.length - 1, levelNumber - 1));
    this.loadLevel(idx);
  }

  private drawPlayer(): void {
    // Скин выбирается один раз в pickRandomSkin(), здесь только применяем
    const skinUrl = this.playerSkinUrl || this.assets.cucumberUrl;
    const tex = Texture.from(skinUrl);
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5, 0.5);
    this.applySpriteSize(sprite, skinUrl);
    this.playerSprite = sprite;
    this.playerView.addChild(sprite);

    // Винтовка поверх скина (как было)
    const rifleTex = Texture.from(this.assets.rifleUrl);
    const rifle = new Sprite(rifleTex);
    rifle.anchor.set(0.33, 0.5);  // pivot у курка
    const rifleH = this.player.height * 0.5;
    const rifleAspect = rifleTex.width / rifleTex.height;
    rifle.height = rifleH;
    rifle.width = rifleH * rifleAspect;
    rifle.y = this.player.height * 0.30;  // на уровне живота — ниже лица
    this.rifleSprite = rifle;
    this.playerView.addChild(rifle);

    // start off-screen until render() places us
    this.playerView.x = -10000;
    this.playerView.y = -10000;
  }

  /** Применить скин игрока (выбирается один раз на матч). */
  private randomizeSkin(): void {
    const skinUrl = this.playerSkinUrl || this.assets.cucumberUrl;
    this.playerSprite.texture = Texture.from(skinUrl);
    this.applySpriteSize(this.playerSprite, skinUrl);
  }

  private getSkinUrl(skin: PlayerSkin): string {
    return this.assets.skinUrlsById[skin] ?? this.assets.cucumberUrl;
  }

  private applyOwnOnlineSkin(room: OnlineSession['room']): void {
    const player = room.players.find(candidate => candidate.id === this.onlineSession?.playerId);
    if (!player) return;

    this.playerSkinUrl = this.getSkinUrl(player.skin);
    if (this.playerSprite) this.randomizeSkin();
  }

  /**
   * Вписываем все скины в коробку 48×60 (Вариант C — «средний» по хитбоксу),
   * аспект сохраняется. Хитбокс физики 32×48, спрайт чуть выпирает (1.5× по
   * ширине, 1.25× по высоте).
   *
   * Дополнительное правило: ширина не меньше playerWidth (32) — иначе очень
   * вытянутые скины (морковь, сельдерей, кукуруза, баклажан) превращаются в
   * нитки тоньше хитбокса. Для них слегка ломаем аспект ради читаемости.
   */
  private applySpriteSize(sprite: Sprite, url: string): void {
    const tex = sprite.texture;
    const aspect = tex.width / tex.height;
    const maxW = this.player.width * 1.5;    // ~48
    const maxH = this.player.height * 1.25;  // ~60
    if (aspect >= maxW / maxH) {
      sprite.width = maxW;
      sprite.height = maxW / aspect;
    } else {
      sprite.height = maxH;
      sprite.width = maxH * aspect;
    }
    // floor по ширине — не уже хитбокса
    const minW = this.player.width;
    if (sprite.width < minW) sprite.width = minW;

    // Per-skin overrides — для скинов которые слишком тонкие/узкие даже после флора.
    // Слегка ломаем аспект ради читаемости в матче.
    if (url.includes('celery')) {
      sprite.width = this.player.width * 1.2;  // ~38
    }
  }

  private ensureRemotePlayer(player: GamePlayerSnapshot) {
    const existing = this.remotePlayers.get(player.id);
    if (existing) return existing;

    const view = new Container();
    const skinUrl = this.getSkinUrl(player.skin);
    const sprite = new Sprite(Texture.from(skinUrl));
    sprite.anchor.set(0.5, 0.5);
    this.applySpriteSize(sprite, skinUrl);
    sprite.alpha = 0.88;
    // tint убран — различаем игроков по цвету ника.
    // Цвет ника устанавливается в applyOnlineSnapshot по реальной команде локального
    // игрока (зелёный для союзников, красный для противников).

    // винтовка как и у локального игрока — pivot у курка, ниже центра
    const rifleTex = Texture.from(this.assets.rifleUrl);
    const rifle = new Sprite(rifleTex);
    rifle.anchor.set(0.33, 0.5);
    const rifleH = this.player.height * 0.5;
    rifle.height = rifleH;
    rifle.width = rifleH * (rifleTex.width / rifleTex.height);
    rifle.y = this.player.height * 0.30;
    const nameText = new Text({
      text: player.name,
      style: { fontFamily: 'Arial', fontSize: 12, fontWeight: 'bold', fill: 0xffffff, stroke: { color: 0x000000, width: 3 } },
    });
    nameText.anchor.set(0.5, 1);
    nameText.y = -this.player.height / 2 - 10;

    // HP-бар выше ника, чтобы не перекрывался текстом
    const hpBg = new Graphics();
    const hpFill = new Graphics();
    const hpY = -this.player.height / 2 - 32;
    hpBg.y = hpY;
    hpFill.y = hpY;

    view.addChild(sprite);
    view.addChild(rifle);
    view.addChild(hpBg);
    view.addChild(hpFill);
    view.addChild(nameText);
    this.world.addChild(view);

    const remote = {
      view, sprite, rifle, hpBg, hpFill, nameText,
      skin: player.skin,
      hp: player.hp,
      buffer: [] as InterpSample[],
      // Стартовая позиция = текущему снапшоту (хитбокс top-left), потом
      // updateRemoteInterpolation будет её перепонимать.
      interpX: player.x,
      interpY: player.y,
      alive: player.alive,
    };
    this.remotePlayers.set(player.id, remote);
    this.drawRemoteHp(remote);
    return remote;
  }

  private drawRemoteHp(remote: { hp: number; hpBg: Graphics; hpFill: Graphics }): void {
    const max = 5;
    const w = 36, h = 4;
    const fillRatio = Math.max(0, Math.min(1, remote.hp / max));
    remote.hpBg.clear()
      .rect(-w / 2 - 1, -1, w + 2, h + 2).fill({ color: 0x000000, alpha: 0.7 });
    remote.hpFill.clear()
      .rect(-w / 2, 0, w * fillRatio, h)
      .fill({ color: remote.hp > 2 ? 0x6ade6a : remote.hp > 0 ? 0xffaa55 : 0xff4444 });
  }

  private clearRemotePlayers(): void {
    for (const remote of this.remotePlayers.values()) {
      remote.view.parent?.removeChild(remote.view);
      remote.view.destroy({ children: true });
    }
    this.remotePlayers.clear();
    this.remoteSnapshots.clear();
  }

  private applyOnlineBullets(bullets: BulletState[]): void {
    const seen = new Set<string>();
    const ownId = this.onlineSession?.playerId;
    for (const b of bullets) {
      // Свои пули рендерим локально через spawnBullet — пропускаем серверный мирор,
      // чтобы не двоилось.
      if (b.ownerId === ownId) continue;
      // Если пуля раньше уже застряла в стене — больше не показываем.
      if (this.deadRemoteBullets.has(b.id)) continue;

      // Локальная стенка: сервер сейчас не проверяет коллизии с платформами,
      // поэтому пуля для нас «гасится» при попадании в любой solid уровня.
      const bb: AABB = { x: b.x - 4, y: b.y - 4, w: 8, h: 8 };
      let hitSolid = false;
      for (const s of this.level.solids) {
        if (aabbOverlap(bb, s)) { hitSolid = true; break; }
      }
      if (hitSolid) {
        this.deadRemoteBullets.add(b.id);
        const existing = this.remoteBullets.get(b.id);
        if (existing) {
          existing.parent?.removeChild(existing);
          existing.destroy();
          this.remoteBullets.delete(b.id);
        }
        continue;
      }

      seen.add(b.id);
      let gfx = this.remoteBullets.get(b.id);
      if (!gfx) {
        gfx = new Graphics()
          .circle(0, 0, 4)
          .fill({ color: 0xff5252 })
          .stroke({ color: 0x000000, width: 1 });
        this.bulletsLayer.addChild(gfx);
        this.remoteBullets.set(b.id, gfx);
      }
      gfx.x = b.x;
      gfx.y = b.y;
    }
    for (const [id, gfx] of this.remoteBullets) {
      if (seen.has(id)) continue;
      gfx.parent?.removeChild(gfx);
      gfx.destroy();
      this.remoteBullets.delete(id);
    }
    // Чистим deadRemoteBullets от id, которые сервер уже забыл — иначе set растёт навсегда.
    if (this.deadRemoteBullets.size > 0) {
      const live = new Set(bullets.map(b => b.id));
      for (const id of this.deadRemoteBullets) {
        if (!live.has(id)) this.deadRemoteBullets.delete(id);
      }
    }
  }

  private clearRemoteBullets(): void {
    for (const gfx of this.remoteBullets.values()) {
      gfx.parent?.removeChild(gfx);
      gfx.destroy();
    }
    this.remoteBullets.clear();
    this.deadRemoteBullets.clear();
  }

  /** Маленький белый крестик на месте попадания, исчезает через 0.3с. */
  private spawnHitCross(x: number, y: number): void {
    const cross = new Graphics();
    const len = 14;
    const w = 3;
    cross.rect(-len / 2, -w / 2, len, w).fill({ color: 0xffffff });
    cross.rect(-w / 2, -len / 2, w, len).fill({ color: 0xffffff });
    cross.x = x;
    cross.y = y;
    cross.alpha = 1;
    this.world.addChild(cross);
    gsap.to(cross, { alpha: 0, duration: 0.28 });
    gsap.to(cross.scale, { x: 1.6, y: 1.6, duration: 0.28, onComplete: () => {
      cross.parent?.removeChild(cross);
      cross.destroy();
    } });
  }

  private applyOnlineSnapshot(players: GamePlayerSnapshot[]): void {
    const ownId = this.onlineSession?.playerId;
    const seen = new Set<PlayerId>();
    // Цвет ника пересчитываем по живой команде локального игрока: союзник зелёный,
    // противник красный. Зрителей и игроков без команды красим как противников —
    // визуальная защита от случая, когда сервер ещё не назначил команду.
    const ownTeam = this.getOwnTeam();
    const TEAMMATE_NICK = 0x44ff88;
    const ENEMY_NICK = 0xff5252;

    const arrivalMs = performance.now();
    for (const player of players) {
      if (player.id === ownId) continue;
      seen.add(player.id);
      this.remoteSnapshots.set(player.id, player);
      const remote = this.ensureRemotePlayer(player);
      if (remote.skin !== player.skin) {
        const skinUrl = this.getSkinUrl(player.skin);
        remote.skin = player.skin;
        remote.sprite.texture = Texture.from(skinUrl);
        this.applySpriteSize(remote.sprite, skinUrl);
      }
      // alive — мгновенно (бинарное состояние, интерполировать не имеет смысла).
      remote.view.visible = player.alive;
      remote.alive = player.alive;
      remote.nameText.text = player.name;
      const nickColor = player.team === ownTeam ? TEAMMATE_NICK : ENEMY_NICK;
      remote.nameText.style.fill = nickColor;
      // HP-бар тоже мгновенно — это UI-индикатор, не визуальная позиция.
      if (remote.hp !== player.hp) {
        remote.hp = player.hp;
        this.drawRemoteHp(remote);
      }

      // Позицию/прицел/направление кладём в буфер интерполяции.
      // updateRemoteInterpolation() в ближайшем fixedStep распакует это в view.x/y.
      remote.buffer.push({
        tMs: arrivalMs,
        x: player.x,
        y: player.y,
        facing: player.facing,
        aimAngle: player.aimAngle,
      });
      if (remote.buffer.length > INTERP_BUFFER_LIMIT) remote.buffer.shift();
    }

    for (const [id, remote] of this.remotePlayers.entries()) {
      if (seen.has(id)) continue;
      remote.view.parent?.removeChild(remote.view);
      remote.view.destroy({ children: true });
      this.remotePlayers.delete(id);
      this.remoteSnapshots.delete(id);
    }
  }

  /**
   * Каждый кадр (60Гц fixedStep) обновляем визуальные позиции удалённых игроков
   * по буферу снапшотов. Рендерим картинку на INTERP_DELAY_MS назад относительно
   * текущего времени, чтобы между двумя соседними снапшотами всегда был известный
   * «следующий» — на нём и lerp-аем. Если данных не хватает (только что зашёл),
   * показываем последний известный без лерпа.
   */
  private updateRemoteInterpolation(): void {
    const halfW = this.player.width / 2;
    const halfH = this.player.height / 2;
    const renderTime = performance.now() - INTERP_DELAY_MS;
    for (const remote of this.remotePlayers.values()) {
      const buf = remote.buffer;
      if (buf.length === 0) continue;

      // Чистим слишком старые семплы (>1с) — они никогда не понадобятся.
      while (buf.length > 2 && buf[1].tMs < renderTime - 1000) buf.shift();

      // Ищем пару семплов [a, b], которая «накрывает» renderTime.
      let a = buf[0];
      let b = buf[0];
      for (let i = 0; i < buf.length - 1; i++) {
        if (buf[i].tMs <= renderTime && buf[i + 1].tMs >= renderTime) {
          a = buf[i];
          b = buf[i + 1];
          break;
        }
      }
      // Если renderTime ещё в будущем относительно всего буфера — берём первую точку.
      // Если renderTime уже позже последней — берём последнюю (без экстраполяции —
      // иначе при пропадании пакетов противник «улетит» дальше реальной позиции).
      if (renderTime < buf[0].tMs) { a = buf[0]; b = buf[0]; }
      else if (renderTime > buf[buf.length - 1].tMs) {
        a = buf[buf.length - 1];
        b = buf[buf.length - 1];
      }

      let x: number, y: number, aim: number;
      let facing: -1 | 1;
      if (a === b || a.tMs === b.tMs) {
        x = a.x; y = a.y; aim = a.aimAngle; facing = a.facing;
      } else {
        const t = clamp01((renderTime - a.tMs) / (b.tMs - a.tMs));
        x = a.x + (b.x - a.x) * t;
        y = a.y + (b.y - a.y) * t;
        aim = lerpAngle(a.aimAngle, b.aimAngle, t);
        // facing — бинарный, не интерполируем; берём от ближайшего «будущего» семпла.
        facing = b.facing;
      }

      remote.interpX = x;
      remote.interpY = y;
      remote.view.x = Math.round(x + halfW);
      remote.view.y = Math.round(y + halfH);
      remote.sprite.scale.x = Math.abs(remote.sprite.scale.x) * facing;
      remote.rifle.rotation = aim;
      const aimingLeft = Math.cos(aim) < 0;
      const sy = Math.abs(remote.rifle.scale.y);
      remote.rifle.scale.y = aimingLeft ? -sy : sy;
      remote.rifle.scale.x = Math.abs(remote.rifle.scale.x);
    }
  }

  private sendOnlineState(): void {
    if (!this.onlineSession) return;

    this.onlineSendTimerMs -= this.loop.stepDt * 1000;
    if (this.onlineSendTimerMs > 0) return;
    this.onlineSendTimerMs = ONLINE_SEND_INTERVAL_MS;

    const playerCenterX = this.player.pos.x + this.player.width / 2;
    const playerCenterY = this.player.pos.y + this.player.height / 2;
    const worldMouseX = this.input.mouseX + this.camera.x;
    const worldMouseY = this.input.mouseY + this.camera.y;
    const state: GamePlayerState = {
      x: this.player.pos.x,
      y: this.player.pos.y,
      vx: this.player.vel.x,
      vy: this.player.vel.y,
      facing: this.player.facing,
      alive: this.player.alive,
      aimAngle: Math.atan2(worldMouseY - playerCenterY, worldMouseX - playerCenterX),
      // hp в state не передаём — сервер авторитетен; игнорируется в RoomManager.updatePlayerState
      hp: this.player.hp,
    };

    this.onlineSession.sendState(state);
  }

  private clearDynamic(): void {
    for (const e of this.enemies) e.destroy();
    this.enemies = [];
    for (const b of this.bullets) b.destroy();
    this.bullets = [];
    if (this.boss) {
      this.boss.destroy();
      this.boss = null;
    }
    this.enemiesLayer.removeChildren();
    this.bulletsLayer.removeChildren();
    this.bossLayer.removeChildren();
    this.playerCarrier = null;
    this.clearRemotePlayers();
    this.clearRemoteBullets();
    this.bossLabel.visible = false;
    this.bossHpBg.clear();
    this.bossHpFill.clear();
    this.won = false;
  }

  private loadLevel(idx: number): void {
    if (this.level) this.world.removeChild(this.level.view);
    this.clearDynamic();
    this.levelIndex = idx;
    const def = this.data.levels[idx]!;
    const levelAssets: LevelAssets = {
      platformHUrl: this.assets.platformHUrl,
      platformVUrl: this.assets.platformVUrl,
      platformMUrl: this.assets.platformMUrl,
      spikeUrl: this.assets.spikeUrl,
    };
    this.level = new Level(def, this.data.config.tileSize, levelAssets);
    this.world.addChildAt(this.level.view, 0);

    for (const enemyDef of def.enemies) {
      const enemy = createEnemy(enemyDef);
      this.enemies.push(enemy);
      this.enemiesLayer.addChild(enemy.gfx);
      enemy.syncGfx();
    }

    if (def.boss) {
      this.boss = new Boss(def.boss);
      this.bossLayer.addChild(this.boss.gfx);
      this.boss.syncGfx();
      this.bossLabel.text = def.boss.name;
      this.bossLabel.visible = true;
    }

    const initialSpawn = this.pickSpawnPoint(this.getOwnTeam());
    this.spawnPoint = { x: initialSpawn.x, y: initialSpawn.y };
    this.player.spawnAt(this.spawnPoint.x, this.spawnPoint.y);
    // сразу синхронизируем позицию playerView чтобы не было телепортации
    this.playerView.x = Math.round(this.player.pos.x + this.player.width / 2);
    this.playerView.y = Math.round(this.player.pos.y + this.player.height / 2);
    this.levelLabel.text = `Уровень ${def.id}: ${def.name}`;
    this.bg.clear()
      .rect(0, 0, this.app.screen.width, this.app.screen.height)
      .fill({ color: hex(def.background) });

    // фон арены
    if (this.bgSprite) { this.bgSprite.parent?.removeChild(this.bgSprite); this.bgSprite.destroy(); this.bgSprite = null; }
    if ((def.winCondition === 'pvp' || def.winCondition === 'ctf') && this.assets.arena1BgUrl) {
      const tex = Texture.from(this.assets.arena1BgUrl);
      this.bgSprite = new Sprite(tex);
      this.bgSprite.width = def.width;
      this.bgSprite.height = this.app.screen.height;
      this.bgSprite.x = 0;
      this.bgSprite.y = 0;
      this.bgSprite.alpha = 0.55; // приглушаем фон
      this.bgLayer.addChild(this.bgSprite);
    }
    // CTF флаги
    if (this.flagSystem) { this.flagSystem.destroy(); this.flagSystem = null; }
    this.flagLayer.removeChildren();
    if (def.winCondition === 'ctf' && def.flags) {
      this.flagSystem = new FlagSystem(this.flagLayer, this.assets.flagRedUrl, this.assets.flagBlueUrl);
      this.flagSystem.init([
        { x: def.flags.red.x, y: def.flags.red.y, team: 'red' },
        { x: def.flags.blue.x, y: def.flags.blue.y, team: 'blue' },
      ]);
      this.scoreText.text = '0 : 0';
      this.scoreText.visible = true;
    } else if (def.winCondition === 'pvp') {
      this.scoreRed = 0;
      this.scoreBlue = 0;
      this.scoreText.text = '0 : 0';
      this.scoreText.visible = true;
    } else {
      this.scoreText.visible = false;
    }

    // таймер матча — отключён, режим бесконечный (online получает значение из снапшота)
    this.matchTimer = 0;
    this.timerText.visible = false;
    

    // статистика матча
    this.matchStats = [
      { nick: getCurrentNickname(), team: 'red', kills: 0, deaths: 0 }
    ];

    // выбираем случайный скин для игрока (уникальный среди всех игроков в матче)
    if (!this.playerSkinUrl) {
      this.pickRandomSkin();
    }
    this.randomizeSkin();

    // тестовые боты
    for (const bot of this.testBots) bot.view.parent?.removeChild(bot.view);
    this.testBots = [];

    // музыка боя
    this.musicIntro.pause();
    this.musicB3.pause();
    this.musicB2.pause();
    this.musicLoop.pause();
    this.musicIntro.currentTime = 0;
    this.musicB3.currentTime = 0;
    this.musicB2.currentTime = 0;
    this.musicLoop.currentTime = 0;
    if ((def.winCondition === 'pvp' || def.winCondition === 'ctf') && this.started && this.musicEnabled) {
      this.musicIntro.play().catch(() => {});
    }

    this.escapeCooldown = 60; // 1 сек задержки перед ESC
    this.respawnTimer = 0;
    this.deathMsg.visible = false;
    this.respawnCountdown.visible = false;
    this.spawnAnim();

    // Online: отдаём сервера платформы текущего уровня — он будет блокировать пули у стен.
    if (this.onlineSession) {
      const solids = this.level.solids.map(s => ({ x: s.x, y: s.y, w: s.w, h: s.h }));
      this.onlineSession.sendLevelSolids(solids);
    }
  }

  private spawnAnim(): void {
    gsap.killTweensOf(this.playerView.scale);
    gsap.fromTo(
      this.playerView.scale,
      { x: 0, y: 0 },
      { x: 1, y: 1, duration: 0.35, ease: 'back.out(2.4)' },
    );
  }

  private fixedStep(): void {
    this.level.step();

    // Интерполяцию удалённых игроков выполняем ДО логики, использующей их
    // позиции (push, локальный bullet hit-check), чтобы коллизии совпадали с
    // тем, что игрок видит на экране.
    if (this.onlineSession) this.updateRemoteInterpolation();

    // TAB — таблица результатов (работает всегда, даже мёртвым)
    if (this.input.isDown('tab')) { this.showTabOverlay(); }
    else { this.hideTabOverlay(); }

    // таймер матча
    if (this.matchTimer > 0 && this.started) {
      this.matchTimer -= this.loop.stepDt * 1000;
      if (this.matchTimer <= 0) {
        this.matchTimer = 0;
        this.endMatchByTimer();
      }
      const totalSec = Math.max(0, Math.ceil(this.matchTimer / 1000));
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      this.timerText.text = `${min}:${sec.toString().padStart(2, '0')}`;
    }

    if (this.respawnTimer > 0) {
      this.respawnTimer -= this.loop.stepDt * 1000;
      const secs = Math.ceil(this.respawnTimer / 1000);
      this.respawnCountdown.text = secs > 0 ? String(secs) : '';
      this.respawnCountdown.visible = secs > 0;
      if (this.respawnTimer <= 0) { this.respawnTimer = -1; this.respawn(); }
      this.stepEnemiesAndBullets(false);
      this.input.endStep();
      return;
    }

    if (!this.player.alive) {
      // скрываем HUD над персонажем
      this.hpBarBg.clear();
      this.hpBarFill.clear();
      this.ammoText.visible = false;
      this.nickText.visible = false;
      this.reloadBg.clear();
      this.reloadFill.clear();
      this.reloadCircle.clear();
      this.input.endStep();
      return;
    }
    this.ammoText.visible = true;

    // T — спавн/убрать болванчика
    if (this.input.wasPressed('spawn')) {
      if (this.dummy.alive) {
        this.dummy.kill();
      } else {
        const facing = this.player.facing;
        const spawnX = this.player.pos.x + facing * 120;
        const spawnY = this.player.pos.y;
        this.dummy.spawn(spawnX, spawnY, -facing as 1 | -1);
      }
    }

    // ESC — выход из боя
    if (this.escapeCooldown > 0) { this.escapeCooldown--; }
    else if (this.started && this.input.wasPressed('escape')) {
      if (this.exitOverlay) { this.hideExitConfirm(); this.input.clearAll(); this.escapeCooldown = 30; }
      else { this.showExitConfirm(); }
    }

    if (this.input.wasPressed('restart')) {
      this.kill();
      this.input.endStep();
      return;
    }

    if (this.playerCarrier) {
      const dx = this.playerCarrier.deltaX();
      const dy = this.playerCarrier.deltaY();
      // применяем delta от платформы через moveAndCollide чтобы не крашить в стены
      if (dx !== 0 || dy !== 0) {
        const box = this.player.bounds();
        const res = moveAndCollide(box, dx, dy, this.level.solids);
        this.player.pos.x = box.x;
        this.player.pos.y = box.y;
        // если платформа прижала к стене — просто останавливаемся, не умираем
        if (res.hitX) this.player.vel.x = 0;
        if (res.hitY) this.player.vel.y = 0;
      }
    }

    // звук прыжка
    if (this.soundEnabled && this.input.wasPressed('jump') && (this.player.onGround || this.player.onWall !== 0 || this.player.jumpsLeft > 0)) {
      this.jumpSound.currentTime = 0;
      this.jumpSound.play().catch(() => {});
    }

    // обновляем флаг несения для штрафа
    if (this.flagSystem) {
      this.player.carryingFlag = this.flagSystem.carriedFlag('player') !== null;
    }
    this.player.step(this.input, this.level.solids);

    this.playerCarrier = this.findCarrier();

    // CTF: обновляем флаги
    if (this.flagSystem) {
      const pb = this.player.bounds();
      // TODO: команда игрока — пока хардкодим 'red' для одиночного теста
      const result = this.flagSystem.update(pb, 'red', this.player.pos.x, this.player.pos.y);
      if (result?.scored) {
        this.scoreText.text = this.flagSystem.scoreRed + ' : ' + this.flagSystem.scoreBlue;
        // уведомляем сервер о захвате флага
        this.onlineSession?.sendFlagCapture(result.team);
      }
      // синхронизируем флаг с позицией носителя
      this.flagSystem.syncCarried('player',
        this.player.pos.x + this.player.width / 2,
        this.player.pos.y  // верхний край hitbox
      );
    }

    // конвертируем mouse coords из screen в world (учитываем камеру)
      const worldMouseX = this.input.mouseX - this.world.x;
      const worldMouseY = this.input.mouseY - this.world.y;
      const shot = this.player.tryShoot(this.input, this.loop.stepDt, worldMouseX, worldMouseY);
    if (shot) {
      this.spawnBullet(new Bullet(shot.x, shot.y, shot.vx, shot.vy, 'player', 4, this.data.config.bulletRange ?? 1024));
      if (this.soundEnabled) { this.pewSound.currentTime = 0; this.pewSound.play().catch(() => {}); }
      // в онлайн-режиме сообщаем серверу о выстреле — пуля появится у других игроков
      this.onlineSession?.sendShoot(shot.x, shot.y, shot.vx, shot.vy, { interpDelayMs: INTERP_DELAY_MS });
    }

    // разрешаем коллизии между персонажами (пуш)
    if (this.player.alive) {
      const allChars: { pos: {x:number,y:number}, width:number, height:number, alive:boolean }[] = [this.player];
      for (const bot of this.testBots) if (bot.alive) allChars.push(bot);
      if (this.dummy.alive) allChars.push(this.dummy);

      for (let i = 0; i < allChars.length; i++) {
        for (let j = i + 1; j < allChars.length; j++) {
          const a = allChars[i];
          const b = allChars[j];
          const ax = a.pos.x, ay = a.pos.y, aw = a.width, ah = a.height;
          const bx = b.pos.x, by = b.pos.y, bw = b.width, bh = b.height;
          const overlapX = (ax + aw) - bx;
          const overlapX2 = (bx + bw) - ax;
          const overlapY = (ay + ah) - by;
          const overlapY2 = (by + bh) - ay;
          if (overlapX > 0 && overlapX2 > 0 && overlapY > 0 && overlapY2 > 0) {
            const minOX = Math.min(overlapX, overlapX2);
            const push = minOX / 2 + 1;
            if (ax < bx) { a.pos.x -= push; b.pos.x += push; }
            else          { a.pos.x += push; b.pos.x -= push; }
          }
        }
      }
    }

    // шаг тестовых ботов
    for (const bot of this.testBots) {
      bot.step(this.loop.stepDt * 1000);
      if (bot.alive) bot.syncGfx();
    }

    // шаг болванчика
    if (this.dummy.alive) {
      this.dummy.physicsStep(this.level.solids);
      const shot = this.dummy.step(this.loop.stepDt * 1000);
      if (shot) this.spawnBullet(new Bullet(shot.x, shot.y, shot.vx, shot.vy, 'enemy', 4, this.data.config.bulletRange ?? 1024));
      this.dummy.syncGfx();
    }

    this.stepBoss();
    this.stepEnemiesAndBullets(true);

    const cfg = this.data.config;
    if (this.player.pos.y > cfg.screenHeight + 200) {
      this.kill(true);
      this.input.endStep();
      return;
    }

    const pb = this.player.bounds();

    // шипы убивают всегда
    for (const s of this.level.spikeBoxes) {
      if (aabbOverlap(pb, s)) {
        this.kill(true);
        this.input.endStep();
        return;
      }
    }

    // враги и босс не убивают во время неуязвимости после респавна
    if (this.player.invincible <= 0) {
      for (const e of this.enemies) {
        if (!e.alive) continue;
        if (aabbOverlap(pb, e.bounds())) {
          this.kill();
          this.input.endStep();
          return;
        }
      }

      if (this.boss && this.boss.alive && aabbOverlap(pb, this.boss.bounds())) {
        this.kill();
        this.input.endStep();
        return;
      }
    }

    const ts = cfg.tileSize;
    for (const c of this.level.def.checkpoints) {
      const cb: AABB = { x: c.x, y: c.y, w: ts, h: ts * 1.5 };
      if (aabbOverlap(pb, cb)) {
        this.spawnPoint = { x: c.x, y: c.y };
      }
    }

    const goal = this.level.def.goal;
    if (goal && this.level.def.winCondition !== 'defeatBoss') {
      const gb: AABB = { x: goal.x, y: goal.y, w: ts, h: ts * 1.5 };
      if (aabbOverlap(pb, gb)) {
        this.nextLevel();
      }
    }

    this.sendOnlineState();
    this.input.endStep();
  }

  private stepBoss(): void {
    if (!this.boss) return;
    if (!this.boss.alive) return;
    const ctx: BossContext = {
      cfg: this.data.config,
      player: this.player,
      solids: this.level.solids,
      spawnBullet: (b) => this.spawnBullet(b),
      stepDt: this.loop.stepDt,
      arenaWidth: this.level.def.width,
    };
    this.boss.step(ctx);
  }

  private spawnBullet(b: Bullet): void {
    this.bullets.push(b);
    this.bulletsLayer.addChild(b.gfx);
  }

  private findCarrier(): import('./MovingPlatform').MovingPlatform | null {
    const playerBottom = this.player.pos.y + this.player.height;
    const playerLeft = this.player.pos.x;
    const playerRight = this.player.pos.x + this.player.width;
    for (const mp of this.level.movingPlatforms) {
      if (Math.abs(playerBottom - mp.aabb.y) > 1.5) continue;
      if (playerRight <= mp.aabb.x || playerLeft >= mp.aabb.x + mp.aabb.w) continue;
      return mp;
    }
    return null;
  }

  private stepEnemiesAndBullets(playerAlive: boolean): void {
    const ctx: EnemyContext = {
      cfg: this.data.config,
      player: this.player,
      solids: this.level.solids,
      spawnBullet: (b) => this.spawnBullet(b),
      stepDt: this.loop.stepDt,
    };
    for (const e of this.enemies) {
      if (e.alive) e.step(ctx);
    }

    for (const b of this.bullets) {
      if (!b.alive) continue;
      b.step();

      if (
        b.x < -200 ||
        b.x > this.level.def.width + 200 ||
        b.y < -400 ||
        b.y > this.data.config.screenHeight + 400
      ) {
        b.alive = false;
        continue;
      }

      const bb = b.bounds();
      let hitWall = false;
      for (const s of this.level.solids) {
        if (aabbOverlap(bb, s)) {
          hitWall = true;
          break;
        }
      }
      if (hitWall) {
        b.alive = false;
        continue;
      }

      if (b.owner === 'player') {
        let consumed = false;
        for (const e of this.enemies) {
          if (!e.alive) continue;
          if (aabbOverlap(bb, e.bounds())) {
            e.hit();
            b.alive = false;
            consumed = true;
            break;
          }
        }
        if (!consumed && this.boss && this.boss.alive && aabbOverlap(bb, this.boss.bounds())) {
          if (this.boss.hit()) this.winGame();
          b.alive = false;
          consumed = true;
        }
        // урон по болванчику
        if (!consumed && this.dummy.alive && aabbOverlap(bb, this.dummy.bounds())) {
          const killed = this.dummy.takeDamage(1);
          if (killed) {
            const me = this.matchStats[0];
            if (me) { me.kills++; this.sendMatchStats(); }
            // TODO: передать victimId когда будут реальные игроки
            this.onlineSession?.sendKill('bot');
            if (this.level.def.winCondition === 'pvp') {
              this.scoreRed++;
              this.scoreText.text = this.scoreRed + ' : ' + this.scoreBlue;
            }
          }
          b.alive = false;
          consumed = true;
        }
        // урон по тестботам
        if (!consumed) {
          for (const bot of this.testBots) {
            if (!bot.alive) continue;
            if (aabbOverlap(bb, bot.bounds())) {
              const killed = bot.takeDamage(1);
              if (killed) {
                const me = this.matchStats[0];
                if (me) me.kills++;
                if (this.level.def.winCondition === 'pvp') {
                  this.scoreRed++;
                  this.scoreText.text = this.scoreRed + ' : ' + this.scoreBlue;
                }
              }
              b.alive = false;
              consumed = true;
              break;
            }
          }
        }
        // онлайн: своя пуля попала в удалённого игрока — гасим визуально (сервер
        // авторитетен по урону, у нас просто исчезает пуля)
        if (!consumed) {
          for (const remote of this.remotePlayers.values()) {
            if (!remote.view.visible) continue;
            const rb: AABB = {
              x: remote.view.x - this.player.width / 2,
              y: remote.view.y - this.player.height / 2,
              w: this.player.width,
              h: this.player.height,
            };
            if (aabbOverlap(bb, rb)) {
              b.alive = false;
              consumed = true;
              break;
            }
          }
        }
      } else if (playerAlive && this.player.alive) {
        if (aabbOverlap(bb, this.player.bounds())) {
          b.alive = false;
          if (this.soundEnabled) { this.hitSound.currentTime = 0; this.hitSound.play().catch(() => {}); }
          this.player.hp = Math.max(0, this.player.hp - 1);
          if (this.player.hp <= 0) {
            // TODO: в мультиплеере — засчитать килл стрелявшему
            this.kill();
          }
          break;
        }
      }
    }

    this.bullets = this.bullets.filter((b) => {
      if (b.alive) return true;
      b.destroy();
      return false;
    });
  }

  private kill(bySpike = false): void {
    if (!this.player.alive) return;
    // смерть — учитываем в статистике
    const me = this.matchStats[0];
    if (me) {
      me.deaths++;
      this.sendMatchStats();
    }
    // в PvP смерть игрока даёт очко врагу
    if (this.level.def.winCondition === 'pvp') {
      this.scoreBlue++;
      this.scoreText.text = this.scoreRed + ' : ' + this.scoreBlue;
    }
    this.deaSound.currentTime = 0;
    this.deaSound.play().catch(() => {});
    this.reloadSound.pause();
    this.reloadSound.currentTime = 0;
    // бросаем флаг при смерти
    if (this.flagSystem) {
      this.flagSystem.dropFlag('player', 'red', this.player.pos.x, this.player.pos.y, bySpike);
      this.player.carryingFlag = false;
    }
    this.player.kill();
    this.deaths += 1;
    this.deathCounter.text = `Смертей: ${this.deaths}`;

    const ruPool = this.level.def.deathMessages ?? this.data.deathMessages;
    const enPool = this.level.def.deathMessagesEn ?? this.data.deathMessagesEn ?? ruPool;
    const pool = this.lang === 'en' ? enPool : ruPool;
    const msg = pool[Math.floor(Math.random() * pool.length)] ?? "You're compost now";

    this.deathMsg.text = msg;
    this.deathMsg.visible = true;
    this.deathMsg.alpha = 0;
    gsap.killTweensOf(this.deathMsg);
    gsap.killTweensOf(this.deathMsg.scale);
    gsap.to(this.deathMsg, { alpha: 1, duration: 0.18, onComplete: () => { gsap.to(this.deathMsg, { alpha: 0, duration: 0.4, delay: this.respawnTimer / 1000 - 0.6 }); } });
    gsap.fromTo(
      this.deathMsg.scale,
      { x: 0.6, y: 0.6 },
      { x: 1, y: 1, duration: 0.45, ease: 'back.out(3)' },
    );

    gsap.killTweensOf(this.app.stage);
    gsap.fromTo(
      this.app.stage,
      { x: -12 },
      { x: 0, duration: 0.4, ease: 'elastic.out(2, 0.25)' },
    );

    gsap.killTweensOf(this.playerView.scale);
    gsap.to(this.playerView.scale, { x: 0, y: 0, duration: 0.25, ease: 'power2.in' });

    this.respawnTimer = this.data.levels[this.levelIndex]?.respawnDelay ?? RESPAWN_DELAY_MS;
  }

  /** Текущая команда локального игрока: онлайн → roomState, иначе local matchStats. */
  private getOwnTeam(): 'red' | 'blue' {
    if (this.onlineSession) {
      const me = this.onlineSession.room.players.find(p => p.id === this.onlineSession?.playerId);
      if (me?.team === 'red' || me?.team === 'blue') return me.team;
    }
    return this.matchStats[0]?.team ?? 'red';
  }

  private pickSpawnPoint(team: 'red' | 'blue'): { x: number; y: number } {
    const def = this.data.levels[this.levelIndex]!;
    const spawns = (def as any).spawns?.[team] as { x: number; y: number }[] | undefined;

    // Fallback если нет точек спавна
    if (!spawns || spawns.length === 0) {
      return { x: def.playerStart.x, y: def.playerStart.y };
    }

    const OCCUPIED_RADIUS = 48; // радиус занятости точки

    // Враги и союзники — из реальных данных + болванчик
    const enemies: { x: number; y: number }[] = [];
    const allies: { x: number; y: number }[] = [];

    for (const snap of this.remoteSnapshots.values()) {
      if (!snap.alive) continue;
      const pos = { x: snap.x, y: snap.y };
      if (snap.team !== team) enemies.push(pos);
      else allies.push(pos);
    }
    // болванчик как враг
    if (this.dummy.alive) enemies.push({ x: this.dummy.pos.x, y: this.dummy.pos.y });

    // Фильтруем занятые точки
    const free = spawns.filter(pt => {
      return !allies.some(a =>
        Math.hypot(a.x - pt.x, a.y - pt.y) < OCCUPIED_RADIUS
      );
    });

    const candidates = free.length > 0 ? free : spawns; // failsafe

    // Выбираем точку с максимальным расстоянием до ближайшего врага
    if (enemies.length === 0) {
      // Нет врагов — случайная точка
      return candidates[Math.floor(Math.random() * candidates.length)];
    }

    let best = candidates[0];
    let bestDist = -1;

    for (const pt of candidates) {
      const minEnemyDist = Math.min(...enemies.map(e =>
        Math.hypot(e.x - pt.x, e.y - pt.y)
      ));
      if (minEnemyDist > bestDist) {
        bestDist = minEnemyDist;
        best = pt;
      }
    }

    return best;
  }

  private respawn(): void {
    this.deathMsg.visible = false;
    // не пересоздаём уровень — мир остаётся как есть (для мультиплеера)
    // просто телепортируем игрока на спавн
    // Выбираем лучшую точку спавна — подальше от врагов, не занятую
    const spawn = this.pickSpawnPoint(this.getOwnTeam());
    // очищаем только пули игрока
    this.bullets = this.bullets.filter(b => {
      if (b.owner === 'player') { b.destroy(); return false; }
      return true;
    });
    this.player.spawnAt(spawn.x, spawn.y);

    // Выталкивание если кто-то стоит на точке спавна
    const pushRadius = this.player.width + 8;
    const occupants: { x: number; y: number }[] = [];
    if (this.dummy.alive) occupants.push({ x: this.dummy.pos.x, y: this.dummy.pos.y });
    // TODO: добавить других игроков в мультиплеере

    for (const occ of occupants) {
      const dx = this.player.pos.x - occ.x;
      const dy = this.player.pos.y - occ.y;
      const dist = Math.hypot(dx, dy);
      if (dist < pushRadius) {
        // Выталкиваем в сторону от занятого игрока
        const dir = dx >= 0 ? 1 : -1;
        this.player.pos.x = occ.x + dir * pushRadius;
      }
    }

    this.playerView.x = Math.round(this.player.pos.x + this.player.width / 2);
    this.playerView.y = Math.round(this.player.pos.y + this.player.height / 2);
    this.randomizeSkin();
    this.input.clearAll();
    this.spawnAnim();
  }

  private nextLevel(): void {
    if (this.levelIndex + 1 >= this.data.levels.length) {
      this.winGame();
      return;
    }
    this.loadLevel(this.levelIndex + 1);
  }

  private pickRandomSkin(): void {
    const skins = this.assets.skinUrls;
    const available = skins.map((_,i) => i).filter(i => !this.usedSkinIndices.has(i));
    const pool = available.length > 0 ? available : skins.map((_,i) => i);
    const idx = pool[Math.floor(Math.random() * pool.length)];
    this.usedSkinIndices.add(idx);
    this.playerSkinUrl = skins[idx];
  }

  setLang(lang: 'ru' | 'en'): void { this.lang = lang; }

  setExitCallback(cb: () => void): void {
    this.exitCallback = cb;
  }

  resetMatch(): void {
    this.playerSkinUrl = '';
    this.usedSkinIndices.clear();
    // убиваем болванчика
    if (this.dummy.alive) this.dummy.kill();
    // сбрасываем HP игрока
    this.player.hp = 5;
    // останавливаем музыку
    this.musicIntro.pause();
    this.musicB3.pause();
    this.musicB2.pause();
    this.musicLoop.pause();
    this.musicIntro.currentTime = 0;
    this.musicB3.currentTime = 0;
    this.musicB2.currentTime = 0;
    this.musicLoop.currentTime = 0;
    this.started = false;
  }

  setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    if (!enabled) {
      this.musicIntro.pause();
      this.musicLoop.pause();
    }
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    const sounds = [this.walkSound, this.pewSound, this.jumpSound, this.hitSound, this.deaSound, this.reloadSound, this.vicSound, this.loseSound];
    sounds.forEach(s => { s.muted = !enabled; });
  }

  /** Мастер-громкость музыки 0..1. Ноль не выключает её — это делает setMusicEnabled. */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    const v = MUSIC_BASE * this.musicVolume;
    this.musicIntro.volume = v;
    this.musicB3.volume = v;
    this.musicB2.volume = v;
    this.musicLoop.volume = v;
  }

  /** Мастер-громкость звуков 0..1. Базовые громкости отдельных эффектов сохраняются. */
  setSoundVolume(volume: number): void {
    this.soundVolume = Math.max(0, Math.min(1, volume));
    this.walkSound.volume = SFX_BASE.walk * this.soundVolume;
    this.vicSound.volume = SFX_BASE.vic * this.soundVolume;
    this.reloadSound.volume = SFX_BASE.reload * this.soundVolume;
    this.pewSound.volume = SFX_BASE.pew * this.soundVolume;
    this.jumpSound.volume = SFX_BASE.jump * this.soundVolume;
    this.hitSound.volume = SFX_BASE.hit * this.soundVolume;
    this.deaSound.volume = SFX_BASE.dea * this.soundVolume;
    this.loseSound.volume = SFX_BASE.lose * this.soundVolume;
  }

  private showExitConfirm(): void {
    if (this.exitOverlay) return;

    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: absolute; inset: 0; background: rgba(0,0,0,0.75);
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      z-index: 200; font-family: Arial, sans-serif; color: #fff;
    `;
    overlay.innerHTML = `
      <div style="background:rgba(20,20,20,0.95);border:2px solid #cc2222;border-radius:12px;padding:36px 48px;text-align:center;">
        <div style="font-size:22px;font-weight:bold;margin-bottom:24px;">Выйти из боя?</div>
        <div style="font-size:14px;color:#888;margin-bottom:28px;">Засчитается поражение</div>
        <div style="display:flex;gap:16px;justify-content:center;">
          <button id="exit-confirm" style="padding:12px 28px;background:rgba(180,20,20,0.8);border:2px solid #ff4444;border-radius:8px;color:#fff;font-size:16px;font-weight:bold;cursor:pointer;">Выйти</button>
          <button id="exit-cancel" style="padding:12px 28px;background:rgba(20,20,20,0.8);border:2px solid #555;border-radius:8px;color:#aaa;font-size:16px;cursor:pointer;">Отмена</button>
        </div>
      </div>
    `;

    const mount = this.app.canvas.parentElement!;
    mount.appendChild(overlay);
    this.exitOverlay = overlay;

    overlay.querySelector('#exit-confirm')!.addEventListener('click', () => {
      this.playLose();
      this.hideExitConfirm();
      // Если идёт онлайн-матч — явно сообщаем серверу leaveRoom (внутри session.close()),
      // чтобы запись игрока не висела в комнате до конца grace и мы не возродились
      // в покинутой комнате при следующем заходе.
      this.onlineSession?.close();
      this.onlineSession = null;
      this.hideLeaveButton();
      this.resetMatch();
      if (this.exitCallback) this.exitCallback();
    });
    overlay.querySelector('#exit-cancel')!.addEventListener('click', () => {
      this.hideExitConfirm();
      this.input.clearAll();
      this.escapeCooldown = 30;
    });
  }

  private hideExitConfirm(): void {
    if (this.exitOverlay) {
      this.exitOverlay.remove();
      this.exitOverlay = null;
    }
  }

  /**
   * Маленькая HTML-кнопка в верхнем правом углу канваса — единственный
   * discoverable способ выйти из онлайн-комнаты. Жмёт тот же showExitConfirm,
   * что и ESC. Показывается только во время онлайн-сессии.
   */
  private showLeaveButton(): void {
    if (this.leaveButton) return;
    const btn = document.createElement('button');
    btn.textContent = this.lang === 'ru' ? '✕ Покинуть' : '✕ Leave';
    btn.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 150;
      padding: 8px 16px;
      background: rgba(20, 20, 20, 0.7);
      border: 2px solid rgba(204, 34, 34, 0.6);
      border-radius: 6px;
      color: #ff6464;
      font-family: Arial, sans-serif;
      font-size: 14px;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.15s;
      user-select: none;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(80, 20, 20, 0.85)';
      btn.style.borderColor = '#ff4444';
      btn.style.color = '#fff';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(20, 20, 20, 0.7)';
      btn.style.borderColor = 'rgba(204, 34, 34, 0.6)';
      btn.style.color = '#ff6464';
    });
    btn.addEventListener('click', () => {
      this.showExitConfirm();
    });
    this.app.canvas.parentElement!.appendChild(btn);
    this.leaveButton = btn;
  }

  private hideLeaveButton(): void {
    if (this.leaveButton) {
      this.leaveButton.remove();
      this.leaveButton = null;
    }
  }

  playLose(): void {
    this.musicIntro.pause();
    this.musicB3.pause();
    this.musicB2.pause();
    this.musicLoop.pause();
    this.loseSound.currentTime = 0;
    this.loseSound.play().catch(() => {});
  }

  private endMatchByTimer(): void {
    const def = this.data.levels[this.levelIndex]!;
    if (def.winCondition === 'ctf') {
      const rs = this.flagSystem?.scoreRed ?? 0;
      const bs = this.flagSystem?.scoreBlue ?? 0;
      if (rs > bs) this.winGame();
      else if (bs > rs) { this.playLose(); this.showEndMsg('DEFEAT'); }
      else { this.showEndMsg('DRAW'); }
    } else if (def.winCondition === 'pvp') {
      if (this.scoreRed > this.scoreBlue) this.winGame();
      else if (this.scoreBlue > this.scoreRed) { this.playLose(); this.showEndMsg('DEFEAT'); }
      else { this.showEndMsg('DRAW'); }
    }
  }

  private showEndMsg(msg: string): void {
    this.deathMsg.text = msg;
    this.deathMsg.visible = true;
    this.deathMsg.alpha = 1;
  }

  private sendMatchStats(): void {
    // TODO: когда сервер поддержит matchStats — отправить через onlineSession
    // this.onlineSession?.sendMatchStats(this.matchStats[0]);
  }

  applyMatchStats(stats: MatchPlayerStats[]): void {
    this.remoteStats.clear();
    for (const s of stats) this.remoteStats.set(s.playerId, s);
  }

  showTabOverlay(): void {
    if (this.tabOverlay) return;
    const overlay = document.createElement('div');
    // Мержим локальную статистику с удалёнными игроками
    const allStats = [...this.matchStats];
    for (const snap of this.remoteSnapshots.values()) {
      if (allStats.find(s => s.nick === snap.name)) continue;
      const remote = this.remoteStats.get(snap.id);
      allStats.push({
        nick: snap.name,
        team: (snap.team === 'red' || snap.team === 'blue') ? snap.team : 'red',
        kills: remote?.kills ?? 0,
        deaths: remote?.deaths ?? 0,
      });
    }
    const sorted = [...allStats].sort((a, b) => b.kills - a.kills);
    const rows = sorted.map((p, i) =>
      `<tr style="color:${p.team === 'red' ? '#ff8080' : '#80b0ff'}">
        <td style="padding:4px 12px">${i + 1}</td>
        <td style="padding:4px 12px">${p.nick}</td>
        <td style="padding:4px 12px;text-align:center">${p.kills}</td>
        <td style="padding:4px 12px;text-align:center">${p.deaths}</td>
        <td style="padding:4px 12px;text-align:center">${p.deaths > 0 ? (p.kills / p.deaths).toFixed(2) : p.kills > 0 ? '∞' : '0'}</td>
      </tr>`
    ).join('');
    overlay.style.cssText = `position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:150;pointer-events:none;`;
    overlay.innerHTML = `
      <div style="background:rgba(10,10,10,0.85);border:2px solid #555;border-radius:10px;padding:20px 32px;min-width:400px;">
        <table style="width:100%;border-collapse:collapse;font-family:Arial;font-size:14px;color:#ccc;">
          <thead>
            <tr style="color:#aaa;border-bottom:1px solid #444;">
              <th style="padding:4px 12px;text-align:left">#</th>
              <th style="padding:4px 12px;text-align:left">Player</th>
              <th style="padding:4px 12px">K</th>
              <th style="padding:4px 12px">D</th>
              <th style="padding:4px 12px">K/D</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    const mount = this.app.canvas.parentElement!;
    mount.appendChild(overlay);
    this.tabOverlay = overlay;
  }

  hideTabOverlay(): void {
    if (this.tabOverlay) { this.tabOverlay.remove(); this.tabOverlay = null; }
  }

  private winGame(): void {
    this.musicIntro.pause();
    this.musicB3.pause();
    this.musicB2.pause();
    this.musicLoop.pause();
    this.vicSound.currentTime = 0;
    this.vicSound.play().catch(() => {});
    if (this.won) return;
    this.won = true;
    this.deathMsg.text = '🏆 GLORIOUS VICTORY!';
    this.deathMsg.visible = true;
    this.deathMsg.alpha = 0;
    gsap.killTweensOf(this.deathMsg);
    gsap.killTweensOf(this.deathMsg.scale);
    gsap.to(this.deathMsg, { alpha: 1, duration: 0.4 });
    gsap.fromTo(
      this.deathMsg.scale,
      { x: 0.4, y: 0.4 },
      { x: 1.1, y: 1.1, duration: 0.6, ease: 'back.out(2.5)' },
    );
    this.player.kill();
  }

  private render(): void {
    const cfg = this.data.config;
    this.camera.follow(
      this.player.pos.x + this.player.width / 2,
      this.player.pos.y + this.player.height / 2,
      this.level.def.width,
      cfg.screenHeight,
    );
    this.world.x = -Math.round(this.camera.x);
    this.world.y = -Math.round(this.camera.y);

    this.playerView.x = Math.round(this.player.pos.x + this.player.width / 2);
    this.playerView.y = Math.round(this.player.pos.y + this.player.height / 2);
    // спрайт смотрит в сторону прицела (мыши), не движения — убирает дёрганье
    // смещаем спрайт: дуло всегда снаружи хитбокса
    // если прилипли к стене — смещаем в сторону стены чтобы не было зазора
    const wallSide = this.player.onWall; // -1, 0, 1
    const offset = wallSide !== 0
      ? wallSide * this.player.width * 0.35  // прилипли — смещаем к стене
      : this.player.facing * this.player.width * 0.35; // свободно — дуло снаружи
    this.playerSprite.x = offset;

    // винтовка целится в курсор: вращаем вокруг рукояти, при aim-left флипаем по Y
    if (this.rifleSprite) {
      const playerCenterX = this.player.pos.x + this.player.width / 2;
      const playerCenterY = this.player.pos.y + this.player.height / 2;
      const worldMouseX = this.input.mouseX + this.camera.x;
      const worldMouseY = this.input.mouseY + this.camera.y;
      const dx = worldMouseX - playerCenterX;
      const dy = worldMouseY - playerCenterY;
      const aimingLeft = dx < 0;
      // флип спрайта по прицелу
      const aimDir = aimingLeft ? -1 : 1;
      this.player.facing = aimDir;
      this.playerSprite.scale.x = Math.abs(this.playerSprite.scale.x) * aimDir;

      // позиция рукояти — у пояса персонажа, без зависимости от facing
      this.rifleSprite.x = 0;
      this.rifleSprite.y = this.player.height * 0.30;

      // rotation — направление дула
      this.rifleSprite.rotation = Math.atan2(dy, dx);

      // scale.x всегда положительный (rotation делает работу), scale.y флипаем при aim-left
      const sx = Math.abs(this.rifleSprite.scale.x);
      const sy = Math.abs(this.rifleSprite.scale.y);
      this.rifleSprite.scale.x = sx;
      this.rifleSprite.scale.y = aimingLeft ? -sy : sy;
    }

    this.coordsText.text = "x=" + Math.round(this.player.pos.x) + " y=" + Math.round(this.player.pos.y);

    // звук перезарядки
    if (this.player.alive && this.player.reloading && this.reloadSound.paused) {
      this.reloadSound.currentTime = 0;
      this.reloadSound.play().catch(() => {});
    } else if ((!this.player.reloading || !this.player.alive) && !this.reloadSound.paused) {
      this.reloadSound.pause();
      this.reloadSound.currentTime = 0;
    }

    // звук ходьбы — только на земле и при движении
    const isWalking = this.player.alive && this.player.onGround &&
      (this.input.isDown('left') || this.input.isDown('right'));
    if (isWalking && this.walkSound.paused) {
      this.walkSound.play().catch(() => {});
    } else if (!isWalking && !this.walkSound.paused) {
      this.walkSound.pause();
      this.walkSound.currentTime = 0;
    }
    for (const e of this.enemies) e.syncGfx();
    for (const b of this.bullets) b.syncGfx();

    // HUD над персонажем — только пока жив
    this.hpBarBg.clear();
    this.hpBarFill.clear();
    this.reloadBg.clear();
    this.reloadFill.clear();
    this.ammoText.visible = this.player.alive;

    if (this.player.alive) {
      const py = this.player.pos.y + this.world.y;
      // Визуальный центр спрайта (учитываем offset от стены)
      const playerCenterX = this.playerView.x + this.world.x + this.playerSprite.x;

      // Ник над HP баром
      const nick = getCurrentNickname();
      this.nickText.text = nick;
      this.nickText.x = playerCenterX;
      this.nickText.y = py - 28;
      this.nickText.visible = true;

      // HP бар
      const hpBarW = 48, hpBarH = 5;
      const hpBarX = playerCenterX - hpBarW / 2;
      const hpBarY = py - 20;
      const hpFrac = Math.max(0, this.player.hp / 5);
      const hpColor = hpFrac > 0.5 ? 0x44dd44 : hpFrac > 0.25 ? 0xffcc00 : 0xff3333;
      this.hpBarBg.roundRect(hpBarX, hpBarY, hpBarW, hpBarH, 2).fill({ color: 0x333333, alpha: 0.8 });
      this.hpBarFill.roundRect(hpBarX, hpBarY, hpBarW * hpFrac, hpBarH, 2).fill({ color: hpColor });

      // Патроны — цифра справа от HP бара, центрирована по anchor(0.5,0.5)
      this.ammoText.text = `${this.player.ammo}`;
      const ammoCX = hpBarX + hpBarW + 14;
      const ammoCY = hpBarY + hpBarH / 2;
      this.ammoText.x = ammoCX;
      this.ammoText.y = ammoCY;

      // Круговой прогресс перезарядки вокруг цифры патронов
      this.reloadBg.clear();
      this.reloadFill.clear();
      this.reloadCircle.clear();
      if (this.player.reloading) {
        const cx = ammoCX;
        const cy = ammoCY;
        const r = 10;
        const progress = 1 - this.player.reloadTimeMs / 2000;
        const startAngle = -Math.PI / 2;
        const endAngle = startAngle + Math.PI * 2 * progress;
        // фон круга
        this.reloadCircle.circle(cx, cy, r).stroke({ color: 0x333333, alpha: 0.8, width: 3 });
        // заполнение дугой
        if (progress > 0) {
          this.reloadCircle.moveTo(cx, cy - r);
          this.reloadCircle.arc(cx, cy, r, startAngle, endAngle);
          this.reloadCircle.stroke({ color: 0xffcc00, width: 3 });
        }
      }
    }
    if (this.boss) {
      this.boss.syncGfx();
      this.drawBossHpBar();
    }
  }

  private drawBossHpBar(): void {
    if (!this.boss || !this.boss.alive) {
      this.bossHpBg.clear();
      this.bossHpFill.clear();
      return;
    }
    const screenW = this.data.config.screenWidth;
    const barW = 480;
    const barH = 14;
    const barX = (screenW - barW) / 2;
    const barY = 30;
    const hpFrac = Math.max(0, this.boss.hp / this.boss.def.hp);

    this.bossHpBg
      .clear()
      .rect(barX - 2, barY - 2, barW + 4, barH + 4)
      .fill({ color: 0x000000 })
      .rect(barX, barY, barW, barH)
      .fill({ color: 0x4a0000 });

    this.bossHpFill
      .clear()
      .rect(barX, barY, barW * hpFrac, barH)
      .fill({ color: 0xff3333 });
  }
}
